import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { onRequestError } from 'services/handlers';

import {
  getColumnOptions,
  populateDataWithDuplicates
} from 'components/pages/patient-source/PmWorkbook/PMWorkbookServices';

import PMWorkBookApi from '../../../../api/patient/PMWorkBookApi';
import NotificationManager from '../../../../common/notifications/NotificationManager';

export const PMWorkbookContext = createContext(null);

export const PMWorkbookProvider = ({ children }) => {
  const apiRef = useGridApiRef();
  const [tableData, setTableData] = useState([]);
  const [columnOptions, setColumnOptions] = useState({});
  const [tableDataLoaded, setTableDataLoaded] = useState(false);

  const refreshData = useCallback(() => {
    PMWorkBookApi.getWorkBookData()
      .then(({ data }) => {
        setTableData(populateDataWithDuplicates(data) || []);
        setColumnOptions(getColumnOptions(data || []));
        setTableDataLoaded(true);
      })
      .catch(err => onRequestError(err));
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const normalizeFieldValue = useCallback((newRow, changedField) => {
    let columnValue = cloneDeep(newRow)[changedField];

    const dateFields = [
      'orderInitiated',
      'redrawOrderInitiated',
      'year1OrderInitiated',
      'year1RedrawOrderInitiated',
      'year2OrderInitiated',
      'year2RedrawOrderInitiated'
    ];
    const dropdownFields = [
      'orderStatus',
      'redrawOrderStatus',
      'year1OrderStatus',
      'year1RedrawOrderStatus',
      'year2OrderStatus',
      'year2RedrawOrderStatus'
    ];
    if (dateFields.includes(changedField)) {
      return moment(columnValue).isValid() ? moment(columnValue).format('YYYY-MM-DD') : null;
    }
    if (dropdownFields.includes(changedField) && columnValue === 'Select') {
      return null;
    }
    return columnValue?.trim();
  }, []);

  const processRowUpdate = useCallback(
    async (newRow, oldRow) => {
      const changedField = Object.keys(newRow).find(key => newRow[key] !== oldRow[key]);
      const normalizedChangedFieldValue = normalizeFieldValue(newRow, changedField);
      if (!changedField || normalizedChangedFieldValue === oldRow[changedField]) {
        apiRef?.current?.resetRowHeights();
        return oldRow;
      }

      try {
        const response = await PMWorkBookApi.updateCell({
          sitePatientId: newRow.ssuPatientId,
          columnName: changedField,
          columnValue: normalizedChangedFieldValue
        }).catch(err => onRequestError(err));

        const updatedRow = {
          ...newRow,
          [changedField]: response.data.toString()
        };

        setTableData(prevState => prevState.map(row => (row.ssuPatientId === newRow.ssuPatientId ? updatedRow : row)));
        apiRef?.current?.resetRowHeights();

        return updatedRow;
      } catch (error) {
        NotificationManager.error('Failed to update cell.');
        apiRef?.current?.resetRowHeights();
        return oldRow;
      }
    },
    [apiRef, normalizeFieldValue]
  );

  const value = useMemo(
    () => ({
      tableData,
      processRowUpdate,
      apiRef,
      tableDataLoaded,
      columnOptions,
      refreshData
    }),
    [tableData, processRowUpdate, apiRef, tableDataLoaded, columnOptions, refreshData]
  );

  return <PMWorkbookContext.Provider value={value}>{children}</PMWorkbookContext.Provider>;
};

export const withPMWorkbookContext = Component => {
  return function WrapperComponent(props) {
    return (
      <PMWorkbookProvider>
        <Component {...props} />
      </PMWorkbookProvider>
    );
  };
};
